import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames/bind'
import enGB from 'date-fns/locale/en-GB'
import fr from 'date-fns/locale/fr'
import es from 'date-fns/locale/es'
import de from 'date-fns/locale/de'
import ptBR from 'date-fns/locale/pt-BR'
import it from 'date-fns/locale/it'
import DatePicker, { registerLocale } from 'react-datepicker'
import {useForm, Controller} from 'react-hook-form'

import {filters} from '../../../../drupalSettings';
import ModuleDropdown from '../../../../components/ModuleDropdown';

import styles from './ModuleDatesForm.module.scss'
import {URLDateParam} from '../../../../../../components/searchHomepageWithFilters/utils';
import offersMockedData from '../../../../../../components/searchHomepageWithFilters/helpers/drupalSettings';
import {isEmpty} from 'ramda';
import {wordings, getDateText} from '../../SearchModuleDates';

const cx = classnames.bind(styles);
const locale = filters?.locale
const nightsText = {
  single: filters?.dates?.night,
  multiple: filters?.dates?.nights,
}
const formId = 'ModuleDatesForm';

const locationMap = {
  'en': {name: 'en-gb', file: enGB},
  'fr': {name: 'fr', file: fr},
  'es': {name: 'es', file: es},
  'de': {name: 'de', file: de},
  'pt-br': {name: 'pt-BR', file: ptBR},
  'it': {name: 'it', file: it},
  default: {name: 'en-gb', file: enGB},
}
const getLocation = (key = locale) => key in locationMap ? locationMap[key] : locationMap.default

registerLocale(getLocation().name, getLocation().file)

const ModuleDatesForm = function ({
  data = [],
  onSubmit,
  onClose,
}) {
  const [fromDate, toDate] = data;
  const [nightCount, setNightCount] = useState()
  const [isOffer, setIsOffer] = useState(false)
  const [monthToDisplay, setMonthToDisplay] = useState(6)
  const wrapperRef = useRef(null)

  const {
    control,
    handleSubmit,
    register,
    watch,
    formState: { errors, isValid, ...other },
  } = useForm({
    defaultValues: {
      dates: data,
    }
  });
  const tempDates = watch('dates');
  const onModalClose = function () {
    onClose && onClose()
  }

  const onFormSubmit = function ({dates}) {
    onSubmit && onSubmit(dates)
  }

  const calculateHoveredNights = (hoveredDay) => {
    const dates = tempDates;

    if (dates && dates[0]) {
      const difference = hoveredDay.getTime() - dates[0].getTime();
      const nightCount = Math.ceil(difference / (1000 * 3600 * 24));
      // Update the hovered day and count the number of nights
      let textNights = nightsText.multiple
      if (nightCount === 1) {
        textNights = nightsText.single
      }
      setNightCount(`${nightCount} ${textNights}`)
    }
  }

  const handleDayMouseEnter = (hoveredDay) => {
    const dates = tempDates;
    if (dates && dates[1]) return // no need to calculate nights if there is an end date

    calculateHoveredNights(hoveredDay)
  }

  const handleDayClass = (date) => {
    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0)
    const today = new Date().setHours(0, 0, 0, 0)
    const formattedDate = URLDateParam(date)

    if (date.getTime() < today) {
      return "react-datepicker__day--disabled"
    }

    if (date.getDate() === lastDay.getDate()) {
      return 'last-day'
    }

    if (offersMockedData && !isEmpty(offersMockedData)) {
      const { from, to } = offersMockedData

      const url = new URL(window.location.href)
      const offerCity = url.searchParams.get("offerCity")
      setIsOffer(offerCity)

      if ((!!from && !!to && formattedDate >= from && formattedDate <= to) && offerCity) {
        return "offer"
      }
    }

    return false
  }

  const onWrapperScroll = () => {
    const scrollableDiv = wrapperRef.current;
    const scrollTop = scrollableDiv.scrollTop;
    const scrollHeight = scrollableDiv.scrollHeight;
    const clientHeight = scrollableDiv.clientHeight;

    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
    if (scrollPercentage >= 0.8) {
      setMonthToDisplay(count => {
        if (count >= 18) {
          wrapperRef.current.removeEventListener('scroll', onWrapperScroll)
        }
        return count + 6
      })
    }
  }

  useEffect(() => {
    wrapperRef.current.addEventListener('scroll', onWrapperScroll)

    if (tempDates[1]) {
      calculateHoveredNights(tempDates[1])
    }
  }, []);

  return (
    <ModuleDropdown
      title={filters?.dates?.label}
      onClose={onModalClose}
      formId={formId}
      submitTxt={filters?.dates?.apply}
      submitDisabled={!isValid}
    >
      <div className={cx('wrapper')} ref={wrapperRef}>
        <form id={formId} onSubmit={handleSubmit(onFormSubmit)}>
          <div className={cx('dates-preview')}>
            <div className={cx('preview')}>
              <span className={cx('date-label')}>{wordings.dateInPlaceholder}</span>
              <span className={cx('date-value')}>{getDateText(tempDates[0])}</span>
            </div>
            <i aria-hidden className={cx('separator', 'icon-dates-arrow')}/>
            <div className={cx('preview')}>
              <span className={cx('date-label')}>{wordings.dateOutPlaceholder}</span>
              <span className={cx('date-value')}>{getDateText(tempDates[1])}</span>
            </div>
          </div>
          <div className="filter-dates filter-dates--blue filter-dates--no-navigation">
            <Controller
              control={control}
              name="dates"
              rules={{required: true}}
              render={({field}) => (
                <DatePicker
                  className="filter-dates__calendar"
                  onChange={field.onChange}
                  selected={field.value?.[0]}
                  startDate={field.value?.[0]}
                  endDate={field.value?.[1]}
                  minDate={new Date()}
                  selectsRange
                  inline
                  monthsShown={monthToDisplay}
                  formatWeekDay={nameOfDay => nameOfDay.substr(0, 3)}
                  disabledKeyboardNavigation
                  shouldCloseOnSelect={false}
                  locale={getLocation().name}
                  dayClassName={handleDayClass}
                  renderDayContents={(day, date) => {
                    return (
                      <div
                        data-nights={nightCount}
                        className="custom-hover-event-day"
                        role={'button'}
                        onMouseEnter={() => handleDayMouseEnter(date)}
                      >
                        {day}
                      </div>
                    )
                  }}
                />
              )}
            />
          </div>
        </form>
      </div>
    </ModuleDropdown>
  )
}

ModuleDatesForm.propTypes = {
  data: PropTypes.any
}

export default ModuleDatesForm;
